import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Alert,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const ReceptionistPaymentsPage = () => {
  const { t } = useTranslation(['receptionist', 'common']);

  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentTokenSearch, setPaymentTokenSearch] = useState('');
  const [searchedPayment, setSearchedPayment] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    payment: null,
    amount: '',
    paymentMethod: 'cash',
    receiptNumber: '',
    notes: ''
  });
  const [confirming, setConfirming] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    paymentType: 'all'
  });

  useEffect(() => {
    fetchPendingPayments();
  }, [filters]);

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (filters.paymentType !== 'all') {
        params.paymentType = filters.paymentType;
      }

      const response = await api.get('/receptionist/payments/pending', { params });
      setPendingPayments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching pending payments:', err);
      setError(err.response?.data?.error || 'Failed to fetch pending payments');
    } finally {
      setLoading(false);
    }
  };

  const searchPaymentByToken = async () => {
    if (!paymentTokenSearch.trim()) {
      setError('Please enter a payment token');
      return;
    }

    try {
      setSearchLoading(true);
      setError('');

      const response = await api.get(`/receptionist/payments/lookup/${paymentTokenSearch.trim()}`);
      setSearchedPayment(response.data.data);
    } catch (err) {
      console.error('Error searching payment:', err);
      setError(err.response?.data?.error || 'Payment not found');
      setSearchedPayment(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const openConfirmDialog = (payment) => {
    setConfirmDialog({
      open: true,
      payment,
      amount: payment.amount.toString(),
      paymentMethod: 'cash',
      receiptNumber: '',
      notes: ''
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      payment: null,
      amount: '',
      paymentMethod: 'cash',
      receiptNumber: '',
      notes: ''
    });
  };

  const confirmPayment = async () => {
    try {
      setConfirming(true);
      setError('');

      const confirmData = {
        amount: parseFloat(confirmDialog.amount),
        paymentMethod: confirmDialog.paymentMethod,
        receiptNumber: confirmDialog.receiptNumber || undefined,
        notes: confirmDialog.notes || undefined
      };

      await api.post(`/receptionist/payments/${confirmDialog.payment._id}/confirm`, confirmData);

      // Refresh data
      await fetchPendingPayments();
      if (searchedPayment && searchedPayment._id === confirmDialog.payment._id) {
        setSearchedPayment(null);
        setPaymentTokenSearch('');
      }

      closeConfirmDialog();
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError(err.response?.data?.error || 'Failed to confirm payment');
    } finally {
      setConfirming(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      membership: 'Membership',
      class: 'Class',
      next_plan: 'Next Plan'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const PaymentCard = ({ payment }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" gutterBottom>
              {payment.user?.firstName} {payment.user?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {payment.user?.email}
            </Typography>
            <Chip
              label={getPaymentTypeLabel(payment.paymentType)}
              size="small"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h5" color="primary">
              {formatCurrency(payment.amount)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Token: {payment.paymentToken}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expires: {formatDate(payment.expiresAt)}
            </Typography>
          </Grid>
        </Grid>

        {payment.paymentType === 'next_plan' && payment.nextPlanPurchase?.selectedPlan && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              Next Plan: {payment.nextPlanPurchase.selectedPlan.name}
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PaymentIcon />}
          onClick={() => openConfirmDialog(payment)}
        >
          Confirm Payment
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('receptionist:payments.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Payment Token Search */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Payment by Token
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label={t('admin.receptionist.payments.paymentToken.label', { ns: 'admin' })}
              value={paymentTokenSearch}
              onChange={(e) => setPaymentTokenSearch(e.target.value)}
              placeholder={t('admin.receptionist.payments.paymentToken.placeholder', { ns: 'admin' })}
              onKeyPress={(e) => e.key === 'Enter' && searchPaymentByToken()}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={searchPaymentByToken}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        {searchedPayment && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Search Result:
            </Typography>
            <PaymentCard payment={searchedPayment} />
          </Box>
        )}
      </Paper>

      {/* Pending Payments */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Pending In-Place Payments
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('admin.receptionist.payments.paymentType.label', { ns: 'admin' })}</InputLabel>
              <Select
                value={filters.paymentType}
                onChange={(e) => setFilters({ ...filters, paymentType: e.target.value })}
                label={t('admin.receptionist.payments.paymentType.label', { ns: 'admin' })}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="membership">Membership</MenuItem>
                <MenuItem value="class">Class</MenuItem>
                <MenuItem value="next_plan">Next Plan</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={fetchPendingPayments} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : pendingPayments.length === 0 ? (
          <Alert severity="info">
            No pending in-place payments found
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {pendingPayments.map((payment) => (
              <Grid key={payment._id} size={{ xs: 12, lg: 6 }}>
                <PaymentCard payment={payment} />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Confirm Payment
        </DialogTitle>
        <DialogContent>
          {confirmDialog.payment && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={t('admin.receptionist.payments.table.customer', { ns: 'admin' })}
                    secondary={`${confirmDialog.payment.user?.firstName} ${confirmDialog.payment.user?.lastName}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('admin.receptionist.payments.table.paymentToken', { ns: 'admin' })}
                    secondary={confirmDialog.payment.paymentToken}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('admin.receptionist.payments.table.type', { ns: 'admin' })}
                    secondary={getPaymentTypeLabel(confirmDialog.payment.paymentType)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('admin.receptionist.payments.table.expectedAmount', { ns: 'admin' })}
                    secondary={formatCurrency(confirmDialog.payment.amount)}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Confirmation Details
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('admin.receptionist.payments.amountReceived.label', { ns: 'admin' })}
                    type="number"
                    value={confirmDialog.amount}
                    onChange={(e) => setConfirmDialog({ ...confirmDialog, amount: e.target.value })}
                    InputProps={{
                      startAdornment: '₺'
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('admin.receptionist.payments.paymentMethod.label', { ns: 'admin' })}</InputLabel>
                    <Select
                      value={confirmDialog.paymentMethod}
                      onChange={(e) => setConfirmDialog({ ...confirmDialog, paymentMethod: e.target.value })}
                      label={t('admin.receptionist.payments.paymentMethod.label', { ns: 'admin' })}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('admin.receptionist.payments.receiptNumber.label', { ns: 'admin' })}
                    value={confirmDialog.receiptNumber}
                    onChange={(e) => setConfirmDialog({ ...confirmDialog, receiptNumber: e.target.value })}
                    placeholder={t('admin.receptionist.payments.receiptNumber.placeholder', { ns: 'admin' })}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('admin.receptionist.payments.notes.label', { ns: 'admin' })}
                    value={confirmDialog.notes}
                    onChange={(e) => setConfirmDialog({ ...confirmDialog, notes: e.target.value })}
                    placeholder={t('admin.receptionist.payments.notes.placeholder', { ns: 'admin' })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPayment}
            disabled={confirming || !confirmDialog.amount}
            startIcon={confirming ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {confirming ? 'Confirming...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReceptionistPaymentsPage;

